import { API } from '@editorjs/editorjs';
import { CodeEditorBlockConfig } from '..';
import CodeEditorBuilder from '../builders/code-editor-builder';
import { CodeEditorBlockToolData } from '../types/data';
import { ICodeEditorFactory } from '../interfaces/CodeEditorFactory';

type ChangeCallbacks = {
  onChangeLanguage: (language: string) => void;
  onRunCode: () => Promise<void>;
  onChangeCode: (code: string) => void;
  clearCode: () => void;
};

export default class BuildCodeEditorFacade {
  private readonly builder: CodeEditorBuilder;
  constructor(
    container: HTMLElement,
    private readonly data: CodeEditorBlockToolData,
    private readonly config: CodeEditorBlockConfig,
    private readonly api: API,
    private readonly codeEditorFactory: ICodeEditorFactory,
  ) {
    this.builder = new CodeEditorBuilder(container, this.api.i18n);
  }

  public getExecutionContainerId() {
    return 'execution-container';
  }

  create(callbacks: ChangeCallbacks) {
    if (this.api.readOnly.isEnabled) {
      return this.createReadOnlyCodeEditor(callbacks);
    }

    if (!this.config.callbacks?.onRunCode) {
      return this.createWithCodeEditorAndSelect(callbacks);
    }

    return this.createFullCodeEditor(callbacks);
  }

  private createReadOnlyCodeEditor({
    onChangeCode,
    onRunCode,
    clearCode,
  }: Pick<ChangeCallbacks, 'onChangeCode' | 'onRunCode' | 'clearCode'>) {
    return this.builder
      .withCodeEditor(this.data, this.codeEditorFactory, {
        onChangeCode,
      })
      .withExecutionContainer(this.getExecutionContainerId())
      .withCodeRunner(onRunCode)
      .withResetButton(clearCode)
      .build();
  }

  private createWithCodeEditorAndSelect({
    onChangeLanguage,
    onChangeCode,
  }: Pick<ChangeCallbacks, 'onChangeLanguage' | 'onChangeCode'>) {
    return this.builder
      .withLanguageSelect(
        this.data.language,
        this.config.languages || [],
        onChangeLanguage,
      )
      .withDivider()
      .withCodeEditor(this.data, this.codeEditorFactory, {
        onChangeCode,
      })
      .build();
  }

  private createFullCodeEditor({
    onChangeCode,
    onChangeLanguage,
    onRunCode,
    clearCode,
  }: ChangeCallbacks) {
    return this.builder
      .withLanguageSelect(
        this.data.language,
        this.config.languages || [],
        onChangeLanguage,
      )
      .withDivider()
      .withCodeEditor(this.data, this.codeEditorFactory, {
        onChangeCode,
      })
      .withExecutionContainer(this.getExecutionContainerId())
      .withCodeRunner(onRunCode)
      .withResetButton(clearCode)
      .build();
  }
}
